/**
 * Phase 19.6 — Employee self-service profile page.
 * Only accessible to employee_portal users with `can_view_own_profile`.
 * Editing requires `can_edit_own_profile`. Owner + delegated Asst-Mgrs receive
 * a Notification on save (server-side).
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Save, UserCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EDITABLE_FIELDS = [
  { key: "preferred_name", label: "Preferred Name" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
  { key: "address", label: "Address" },
  { key: "emergency_contact_name", label: "Emergency Contact Name" },
  { key: "emergency_contact_phone", label: "Emergency Contact Phone" },
];

export default function MyProfile() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [perms, setPerms] = useState({ is_admin: false, permissions: {} });
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const permsRes = await axios.get(`${API}/portal/me/permissions`);
      setPerms(permsRes.data);
      // Find employee id — use user.linked_employee_id if present, else /api/users/me
      let eid = user?.linked_employee_id;
      if (!eid) {
        try {
          const me = await axios.get(`${API}/users/me`);
          eid = me?.data?.linked_employee_id;
        } catch {}
      }
      if (!eid) {
        toast.error("Your portal account is not linked to an employee record.");
        setLoading(false);
        return;
      }
      const empRes = await axios.get(`${API}/employees/${eid}`);
      setEmployee(empRes.data);
      const initialForm = {};
      EDITABLE_FIELDS.forEach((f) => { initialForm[f.key] = empRes.data?.[f.key] || ""; });
      setForm(initialForm);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed to load your profile");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const canEdit = perms.is_admin || !!perms.permissions?.can_edit_own_profile;
  const canView = perms.is_admin || !!perms.permissions?.can_view_own_profile;

  const onSave = async () => {
    if (!canEdit) {
      toast.error("You do not have permission to edit your profile.");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/employees/me/profile`, form);
      setEmployee(res.data);
      toast.success("Profile updated. Your manager has been notified.");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <Card>
          <CardHeader>
            <CardTitle>Access denied</CardTitle>
            <CardDescription>
              You do not have the <code>can_view_own_profile</code> permission. Contact your manager.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <Card data-testid="my-profile-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCircle className="h-5 w-5 text-primary" /> My Profile
          </CardTitle>
          <CardDescription>
            Keep your contact info up to date. Changes notify your manager automatically.
            {!canEdit && (
              <span className="block mt-1">
                <Badge variant="outline">View-only</Badge> Ask your manager to enable editing.
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {employee && (
            <div className="rounded-lg border bg-muted/30 p-3 text-sm">
              <p><span className="text-muted-foreground">Name:</span> <span className="font-medium">{employee.name}</span></p>
              {employee.position && (
                <p><span className="text-muted-foreground">Position:</span> {employee.position}</p>
              )}
              {employee.department && (
                <p><span className="text-muted-foreground">Department:</span> {employee.department}</p>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {EDITABLE_FIELDS.map((f) => (
              <div key={f.key} className={f.key === "address" ? "sm:col-span-2" : ""}>
                <Label htmlFor={`field-${f.key}`}>{f.label}</Label>
                <Input
                  id={`field-${f.key}`}
                  value={form[f.key] || ""}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  disabled={!canEdit}
                  data-testid={`my-profile-${f.key}`}
                />
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <Button onClick={onSave} disabled={!canEdit || saving} data-testid="my-profile-save-btn">
              <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
